import { useEffect, useRef } from "react";

/**
 * Custom baseball cursor with a lagging red glow trail.
 * `size` scales the whole cursor (1 = homepage, 0.75 = briefing page).
 * Disabled on touch devices and when the user prefers reduced motion.
 */
export function BaseballCursor({ size = 1 }) {
  const ballRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (!finePointer || reducedMotion) return undefined;

    document.body.classList.add("bsc-cursor-active");
    const ball = ballRef.current;
    const glow = glowRef.current;

    let mx = -100;
    let my = -100;
    let gx = -100;
    let gy = -100;
    let prevX = -100;
    let prevY = -100;
    let lvx = 0;
    let lvy = 0;
    let scale = 1;
    let targetScale = 1;
    let raf;

    const onMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      const t = e.target;
      targetScale =
        t && t.closest &&
        t.closest("a, button, [role='button'], input, textarea, select, summary, label")
          ? 1.55
          : 1;
    };

    const loop = () => {
      // Smoothed velocity gives the glow's "behind" direction. The glow is
      // hard-clamped to a small radius around the ball so it can never
      // separate, even on fast mouse movement.
      const dx = mx - prevX;
      const dy = my - prevY;
      prevX = mx;
      prevY = my;
      lvx += (dx - lvx) * 0.35;
      lvy += (dy - lvy) * 0.35;
      const speed = Math.hypot(lvx, lvy);

      let tx = mx;
      let ty = my;
      if (speed > 0.3) {
        const off = Math.min(8 + speed * 0.8, 16) * size;
        tx = mx - (lvx / speed) * off;
        ty = my - (lvy / speed) * off;
      }

      gx += (tx - gx) * 0.5;
      gy += (ty - gy) * 0.5;

      // Never let the glow drift more than 18px (scaled) from the ball.
      const sx = gx - mx;
      const sy = gy - my;
      const dist = Math.hypot(sx, sy);
      const maxDist = 18 * size;
      if (dist > maxDist) {
        gx = mx + (sx / dist) * maxDist;
        gy = my + (sy / dist) * maxDist;
      }

      scale += (targetScale - scale) * 0.2;
      if (ball) {
        ball.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%, -50%) scale(${scale * size})`;
      }
      if (glow) {
        glow.style.transform = `translate3d(${gx}px, ${gy}px, 0) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
      document.body.classList.remove("bsc-cursor-active");
    };
  }, [size]);

  return (
    <div aria-hidden="true" data-testid="baseball-cursor">
      <div
        ref={glowRef}
        className="bsc-cursor-glow"
        style={{ width: 46 * size, height: 46 * size }}
      />
      <div ref={ballRef} className="bsc-cursor-ball">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <circle cx="11" cy="11" r="9.6" fill="#ffffff" stroke="#082848" strokeWidth="1" />
          <path d="M4.4 3.6 C 8.2 7.4, 8.2 14.6, 4.4 18.4" stroke="#C62C29" strokeWidth="1.3" strokeLinecap="round" />
          <path d="M17.6 3.6 C 13.8 7.4, 13.8 14.6, 17.6 18.4" stroke="#C62C29" strokeWidth="1.3" strokeLinecap="round" />
          <path d="M6.1 6.4 L 7.5 5.9 M6.9 8.6 L 8.4 8.3 M7.2 11 L 8.7 11 M6.9 13.4 L 8.4 13.7 M6.1 15.6 L 7.5 16.1" stroke="#C62C29" strokeWidth="0.8" strokeLinecap="round" />
          <path d="M15.9 6.4 L 14.5 5.9 M15.1 8.6 L 13.6 8.3 M14.8 11 L 13.3 11 M15.1 13.4 L 13.6 13.7 M15.9 15.6 L 14.5 16.1" stroke="#C62C29" strokeWidth="0.8" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}
